import { useMemo } from "react";

export type TocItem = {
  id: string;
  text: string;
  level: number;
  children: TocItem[];
};

function buildTocTree(headers: Element[]): TocItem[] {
  const root: TocItem[] = [];
  const stack: { level: number; item: TocItem }[] = [];

  headers.forEach((header) => {
    const level = parseInt(header.tagName[1]);
    const item: TocItem = {
      id: header.id,
      text: header.textContent || "",
      level,
      children: [],
    };

    while (stack.length > 0 && level <= stack[stack.length - 1].level) {
      stack.pop();
    }

    if (stack.length === 0) {
      root.push(item);
    } else {
      stack[stack.length - 1].item.children.push(item);
    }

    stack.push({ level, item });
  });

  return root;
}

export function useToc(shadowRoot: ShadowRoot | null): TocItem[] {
  return useMemo(() => {
    if (!shadowRoot) return [];

    const contentEl = shadowRoot.querySelector(".shadow-content");
    if (!contentEl) return [];

    const headers = contentEl.querySelectorAll(
      "h2[id], h3[id], h4[id], h5[id], h6[id]",
    );
    if (headers.length <= 3) return [];

    return buildTocTree(Array.from(headers));
  }, [shadowRoot]);
}

function findTocTarget(container: Element, targetId: string): Element | null {
  const root = container.getRootNode();
  if ("getElementById" in root) {
    const target = (root as Document | ShadowRoot).getElementById(targetId);
    return target && container.contains(target) ? target : null;
  }

  // Detached containers used before insertion do not have an ID lookup API.
  for (const element of container.querySelectorAll("[id]")) {
    if (element.id === targetId) return element;
  }

  return null;
}

function scrollToTocTarget(container: Element, targetId: string): void {
  const targetElement = findTocTarget(container, targetId);
  if (!targetElement) return;

  // On mobile, expand the section when clicking a TOC item
  if (window.matchMedia("(max-width: 640px)").matches) {
    const section = targetElement.closest(
      'section[data-mw-section-id]:not([data-mw-section-id="0"])',
    );
    if (section) {
      section.classList.add("expanded");
    }
  }

  targetElement.scrollIntoView({ behavior: "instant", block: "start" });
}

export function buildTocDom(container: Element): void {
  // Select all headers and add IDs to those without them
  const allHeaders = container.querySelectorAll("h2, h3, h4, h5, h6");

  // WEIRDLY my buligarian dump didnt have any ids
  allHeaders.forEach((header, index) => {
    if (!header.id) {
      const text = header.textContent?.trim() || `section-${index}`;
      header.id =
        text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || `section-${index}`;
    }
  });

  const headers = container.querySelectorAll(
    "h2[id], h3[id], h4[id], h5[id], h6[id]",
  );
  if (headers.length <= 3) return;

  const toc = document.createElement("fieldset");
  toc.id = "toc";

  const articleBody = container.querySelector("#article_body");
  if (!articleBody) return;

  if (articleBody.firstElementChild) {
    articleBody.firstElementChild.insertAdjacentElement("afterend", toc);
  } else {
    articleBody.insertAdjacentElement("afterbegin", toc);
  }

  const legend = document.createElement("legend");
  legend.textContent = "Table Of Content";
  toc.appendChild(legend);

  const rootUl = document.createElement("ul");
  toc.appendChild(rootUl);

  const stack: { level: number; ul: HTMLUListElement }[] = [
    { level: 1, ul: rootUl },
  ];

  headers.forEach((header) => {
    const level = parseInt(header.tagName[1]);
    const li = document.createElement("li");
    li.classList.add(`level-${level}`);

    while (stack.length > 1 && level <= stack[stack.length - 1].level) {
      stack.pop();
    }

    if (level === 2) {
      li.classList.add("collapsible-item");
      const icon = document.createElement("span");
      icon.classList.add("toggle-icon");
      icon.textContent = "▶";
      li.appendChild(icon);

      const text = document.createElement("span");
      text.textContent = header.textContent || "";
      text.style.cursor = "pointer";
      text.dataset.targetId = header.id;
      li.appendChild(text);

      const newUl = document.createElement("ul");
      newUl.style.display = "none";
      li.appendChild(newUl);
      stack.push({ level, ul: newUl });
    } else {
      li.textContent = header.textContent || "";
      li.style.cursor = "pointer";
      li.dataset.targetId = header.id;
      stack[stack.length - 1].ul.appendChild(li);
    }

    if (level > 2) {
      stack[stack.length - 1].ul.appendChild(li);
    } else if (level === 2) {
      rootUl.appendChild(li);
    }
  });

  const allH2Lis = toc.querySelectorAll("li > .toggle-icon");
  allH2Lis.forEach((icon) => {
    const li = icon.parentElement;
    if (!li) return;
    const nestedUl = li.querySelector("ul");
    if (!nestedUl || nestedUl.children.length === 0) {
      (icon as HTMLElement).style.display = "none";
      li.classList.remove("collapsible-item");
    }
  });

  toc.addEventListener("click", (e) => {
    const target = e.target as Element;
    const li = target.closest("li");

    if (!li) return;

    if (target.classList.contains("toggle-icon")) {
      const nestedUl = li.querySelector("ul");
      if (nestedUl) {
        const isCollapsed = nestedUl.style.display === "none";
        nestedUl.style.display = isCollapsed ? "block" : "none";
        const icon = li.querySelector(".toggle-icon");
        if (icon) icon.textContent = isCollapsed ? "▼" : "▶";
      }
    } else if (target.tagName === "SPAN" && li.querySelector("ul")) {
      const nestedUl = li.querySelector("ul");
      if (nestedUl) {
        const isCollapsed = nestedUl.style.display === "none";
        nestedUl.style.display = isCollapsed ? "block" : "none";
        const icon = li.querySelector(".toggle-icon");
        if (icon) icon.textContent = isCollapsed ? "▼" : "▶";
      }
      const targetId = (target as HTMLElement).dataset.targetId;
      if (targetId) {
        scrollToTocTarget(container, targetId);
      }
    } else if (li.dataset.targetId) {
      scrollToTocTarget(container, li.dataset.targetId);
    }
  });
}

if (import.meta.vitest) {
  const { describe, it, expect, vi } = import.meta.vitest;

  describe("buildTocTree", () => {
    it("builds tree from headers", () => {
      const div = document.createElement("div");
      div.innerHTML = `
        <h2 id="s1">Section 1</h2>
        <h3 id="s1-1">Sub 1.1</h3>
        <h2 id="s2">Section 2</h2>
      `;
      const headers = div.querySelectorAll("h2, h3");
      const tree = buildTocTree(Array.from(headers));

      expect(tree).toHaveLength(2);
      expect(tree[0].text).toBe("Section 1");
      expect(tree[0].children).toHaveLength(1);
      expect(tree[0].children[0].text).toBe("Sub 1.1");
      expect(tree[1].text).toBe("Section 2");
      expect(div).toBeDefined();
    });

    it("returns empty array for no headers", () => {
      const tree = buildTocTree([]);
      expect(tree).toHaveLength(0);
    });
  });

  describe("buildTocDom", () => {
    it("scrolls to headings whose IDs contain apostrophes", () => {
      const container = document.createElement("div");
      container.innerHTML = `
        <div id="article_body">
          <p>Introduction</p>
          <h2 id="Différents_niveaux_d'organisation">Organization</h2>
          <h2 id="section-2">Section 2</h2>
          <h2 id="section-3">Section 3</h2>
          <h2 id="section-4">Section 4</h2>
        </div>
      `;
      const heading = container.querySelector("h2") as HTMLElement;
      heading.scrollIntoView = vi.fn();
      vi.stubGlobal(
        "matchMedia",
        vi.fn(() => ({ matches: false })),
      );

      buildTocDom(container);
      const tocItem = container.querySelector("#toc [data-target-id]");

      expect(tocItem).not.toBeNull();
      expect(() =>
        tocItem?.dispatchEvent(new MouseEvent("click", { bubbles: true })),
      ).not.toThrow();
      expect(heading.scrollIntoView).toHaveBeenCalledWith({
        behavior: "instant",
        block: "start",
      });

      vi.unstubAllGlobals();
    });
  });
}
